import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { clientsApi, lessonsApi } from "@/api";
import Header from "@/components/Header";

const BookLessonScreen = () => {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(clientId || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const dateFromUrl = searchParams.get('date');
  const timeFromUrl = searchParams.get('time');
  
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  useEffect(() => {
    if (dateFromUrl) {
      setSelectedDate(dateFromUrl);
    } else {
      setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
    }
    if (timeFromUrl) {
      setSelectedTime(timeFromUrl);
    }
  }, [dateFromUrl, timeFromUrl]);

  useEffect(() => {
    clientsApi.getAll().then(res => setClients(res.data));
  }, []);

  const timeSlots = [];
  for (let hour = 7; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  const handleSubmit = async () => {
    if (!selectedClient || !selectedDate || !selectedTime) {
      alert(t('common.fillAllFields'));
      return;
    }

    const client = clients.find(c => c.id === selectedClient);
    if (client && client.remaining_lessons <= 0) {
      alert(t('lessons.noAvailableLessons'));
      return;
    }

    const [hours, minutes] = selectedTime.split(':');
    const endHour = parseInt(hours) + 1;
    const endTime = `${endHour.toString().padStart(2, '0')}:${minutes}`;

    setLoading(true);
    try {
      await lessonsApi.create({
        client_id: selectedClient,
        date: selectedDate,
        start_time: selectedTime,
        end_time: endTime,
        notes: notes || null,
      });
      alert(t('lessons.lessonBooked'));
      navigate(-1);
    } catch (error) {
      alert(error.response?.data?.detail || t('lessons.bookingError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header title={t('lessons.bookLesson')} showBack />
      
      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('packages.client')}</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl"
            data-testid="select-client"
          >
            <option value="">{t('packages.selectClient')}</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.first_name} {client.last_name} ({client.remaining_lessons} {t('common.lessons')})
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('lessons.date')}</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={format(new Date(), 'yyyy-MM-dd')}
            className="w-full p-3 border border-gray-200 rounded-xl"
            data-testid="select-date"
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('lessons.time')}</label>
          <div className="grid grid-cols-4 gap-2">
            {timeSlots.map(time => (
              <button
                key={time}
                onClick={() => setSelectedTime(time)}
                className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTime === time
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('lessons.notesOptional')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl"
            rows={3}
            placeholder={t('lessons.addNotes')}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 disabled:bg-gray-400"
          data-testid="confirm-booking"
        >
          {loading ? t('lessons.bookingInProgress') : t('lessons.confirmBooking')}
        </button>
      </div>
    </div>
  );
};

export default BookLessonScreen;
